import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import moment from 'moment';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import Notify from '../../../../../shared/components/widgets/Notify';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';

export default function DspMediaPlans() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [forceRefresh, setForceRefresh] = useState(moment());
  const [agGrid, setAgGrid] = useState(null);
  const [updatedRowCount, setUpdatedRowCount] = useState(0);
  const [originalData, setOriginalData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // function jsonCellRenderer(params) {
  // // Hold  cellRenderer: (params) => <pre style={{ fontFamily: 'monospace', margin: 0, padding: 0, whiteSpace: 'pre-wrap' }}>${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>
  //   return `<pre style="font-family: monospace; margin: 0; padding: 0; white-space: pre-wrap;">${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>`;
  // }

  const resetRow = (orgRowObj) => {
    agGrid.gridApi.forEachNode((rowNode) => {
      if (orgRowObj.id === rowNode.data.id) {
        rowNode.setData(orgRowObj);
        setUpdatedRowCount(curentCount => curentCount - 1);
      }
    });
  };

  const removeRow = (rowData) => {
    agGrid.gridApi.applyTransaction({ remove: [rowData.data] });
    setUpdatedRowCount(curentCount => curentCount - 1);
  };

  const submitDspMP = () => {
    setSubmitting(true);
    const dspMediaPlansUrl = `${config.serverUrl}/management/dsp/media-plans`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const saveData = [];
    // Build changes
    agGrid.gridApi.forEachNode((rowNode) => {
      if (rowNode.data.change || rowNode.data.id === 0) {
        const { change, mediaPlanId, funnelId, sequenceStep, adCreativeVariation, ...rest } = rowNode.data;
        saveData.push({
          mediaPlanId: parseInt(mediaPlanId, 10),
          funnelId: parseInt(funnelId, 10),
          sequenceStep: parseInt(sequenceStep, 10),
          adCreativeVariation: parseInt(adCreativeVariation, 10),
          ...rest,
        }); // Remove fields we don't care about and add to changedRows
      }
    });

    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId: selectedSeller.value,
        saveData,
      }),
    };

    fetch(dspMediaPlansUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then(() => {
        Notify.show('primary', 'Success', 'MediaPlans have been saved!');
        setUpdatedRowCount(0);
        setForceRefresh(moment());
        setSubmitting(false);
      }).catch(() => {
        Notify.show('danger', 'Error', 'We were unable to save your MediaPlans.');
        setForceRefresh(moment());
        setSubmitting(false);
      });
  };

  const submitAssociations = (method, actionStatus) => {
    setSubmitting(true);
    const dspCreateiveAssociation = `${config.serverUrl}/management/dsp/creatives/association`;
    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const requestOptions = {
      method,
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId: selectedSeller.value,
        isLive: 'TRUE',
        actionStatus,
      }),
    };

    fetch(dspCreateiveAssociation, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then(() => {
        Notify.show('primary', 'Success', `Rows with status ${actionStatus} have been saved!`);
        setForceRefresh(moment());
        setSubmitting(false);
      }).catch(() => {
        Notify.show('danger', 'Error', `We were unable to save your rows with ${actionStatus} status.`);
        setForceRefresh(moment());
        setSubmitting(false);
      });
  };


  const highlightChanges = (row) => {
    if ((row.data?.change && row.value !== row.data.change[row.colDef.field]) || (row.data.id === 0)) {
      return { backgroundColor: '#91BE23' };
    }
    return { backgroundColor: '' };
  }

  const columnDefs = [
    {
      field: 'change',
      headerName: 'CHANGE',
      cellStyle: (params) => (params.value !== '' ? { color: '#91BE23' } : { color: 'black' }),
      cellRenderer: (params) => {
        if (params.value && params.data.id > 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => resetRow(params.value)}>Reset Row</button>;
        }
        if (params.data.id === 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => removeRow(params)}>Remove Row</button>;
        }
        return ''
      },
      editable: false,
    },
    { field: 'id', headerName: 'ID', editable: false },
    { field: 'accountId', headerName: 'Account ID', editable: false },
    { field: 'dspAdvertiserId', headerName: 'DSP Advertiser ID', cellStyle: params => highlightChanges(params) },
    { field: 'mediaPlanId', headerName: 'Media Plan ID', cellStyle: params => highlightChanges(params), valueParser: params => Number(params.newValue) },
    { field: 'mediaPlanName', headerName: 'Media Plan Name', cellStyle: params => highlightChanges(params) },
    { field: 'funnelId', headerName: 'Funnel ID', cellStyle: params => highlightChanges(params), valueParser: params => Number(params.newValue) },
    { field: 'sequenceStep', headerName: 'Sequence Step', cellStyle: params => highlightChanges(params), valueParser: params => Number(params.newValue) },
    { field: 'mediaType', headerName: 'Media Type', cellStyle: params => highlightChanges(params) },
    { field: 'sourceLanguage', headerName: 'Source Language', cellStyle: params => highlightChanges(params) },
    { field: 'adCreativeVariation', headerName: 'Ad Creative Variation', cellStyle: params => highlightChanges(params), valueParser: params => Number(params.newValue) },
    { field: 'adCreativeId', headerName: 'Ad Creative ID', cellStyle: params => highlightChanges(params) },
    { field: 'creativeAudienceId', headerName: 'Creative Audience ID', cellStyle: params => highlightChanges(params) },
    { field: 'combinedCreativeAudienceId', headerName: 'Combined Creative Audience ID', cellStyle: params => highlightChanges(params) },
    { field: 'status', headerName: 'Status', cellStyle: params => highlightChanges(params) },
    { field: 'createTs', headerName: 'Create TS', editable: false },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: true,
  };

  const showDatePicker = false;
  const showExportOptions = true;
  const apiEndPoint = `${config.serverUrl}/management/dsp/media-plans`;

  const exportPrefix = getExportPrefix('amz-dsp-media-plans', selectedSeller, true);

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  const parentSetData = (data) => {
    setOriginalData(JSON.parse(JSON.stringify(data)));
  }

  const onRowValueChanged = (cellData) => {
    if (!agGrid) {
      return;
    }
    agGrid.gridApi.forEachNode((rowNode) => {
      if (cellData.data.id === rowNode.data.id) {
        if (!rowNode.data.change) {
          const origData = originalData.find(origRow => origRow.id === cellData.data.id);
          rowNode.setDataValue('change', { ...origData });
          if (cellData.data.id !== 0) {
            setUpdatedRowCount(curentCount => curentCount + 1);
          }
        }
      }
    })
  }

  const getNewRow = () => {
    const newData = {
      id: 0,
      accountId: selectedSeller.value,
      dspAdvertiserId: null,
      mediaPlanId: null,
      mediaPlanName: null,
      funnelId: null,
      sequenceStep: null,
      mediaType: null,
      sourceLanguage: null,
      adCreativeVariation: null,
      adCreativeId: null,
      creativeAudienceId: null,
      combinedCreativeAudienceId: null,
      status: null,
      createTs: null,
    };
    setUpdatedRowCount(curentCount => curentCount + 1);
    return newData;
  }


  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
    }
  }, [forceRefresh]);

  return (
    <Container>
      <Row>
        <TableBuilder
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          apiEndPoint={apiEndPoint}
          showDatePicker={showDatePicker}
          showExportOptions={showExportOptions}
          exportPrefix={exportPrefix}
          parentAccess={parentAccess}
          parentSetData={parentSetData}
          onRowValueChanged={onRowValueChanged}
          showAddRowButton
          getNewRow={getNewRow}
        />
      </Row>
      <Row>
        <CardPanel
          title={`Updated Count: ${updatedRowCount}`}
          autorenewButton={false}
          closeButton={false}
        >
          <div style={{ float: 'left', width: '50%' }}>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitDspMP()}>Save Media Plans</button>
          </div>
          <div style={{ float: 'right', width: '50%' }}>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitAssociations('POST', 'ADD')}>ADD Creatives</button>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitAssociations('PATCH', 'ACTIVATE')}>ACTIVATE Creatives</button>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitAssociations('PATCH', 'INACTIVATE')}>INACTIVATE Creatives</button>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitAssociations('DELETE', 'REMOVE')}>REMOVE Creatives</button>
          </div>
        </CardPanel>
      </Row>
    </Container>
  );
}
